
import Notification from '@/components/helper/Notification.vue'
import JigTextField from '@/components/input/JigTextField.vue'
import PromptYesNo from '@/components/modals/PromptYesNo.vue'
import UploadDocumentsDialog from '@/components/modals/UploadDocumentsDialog.vue'
// Shared upload-state enum keeps parent orchestration and modal progress UI aligned.
import { DocumentUploadState } from '@/components/modals/UploadDocumentsDialog.types'
import FilePreview from '@/components/tenant/FilePreview.vue'
import { SubscriptionConst } from '@/constants'
import { TeamLibraryConst } from '@/constants/teamlibrary'
import { Tenant } from '@/store/modules/app/types'
import { TenantContextDocument, TenantContextDocumentsPagination } from '@/store/modules/tenantcontext/types'
import { Namespace } from '@/store/types'
import { AppHelper } from '@/utils/app-helper'
import { TenantHelpers } from '@/utils/tenant-helpers'
import IconView from '@svg/Icon_View.svg'
import IconBin from '@svg/IconBin.svg'
import IconClose from '@svg/IconClose.svg'
import IconUploadFile from '@svg/IconUploadFile.svg'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { Action, Getter, Mutation, State } from 'vuex-class'

@Component({
  components: {
    Notification,
    JigTextField,
    PromptYesNo,
    UploadDocumentsDialog,
    FilePreview,
    IconView,
    IconBin,
    IconClose,
    IconUploadFile,
  },
})
export default class TenantContentTab extends Vue {
  @Prop({ required: true }) editedTenant!: Tenant
  @Prop({ type: Boolean, default: false }) lacksTenantManagePermission!: boolean

  @State('apiError', { namespace: Namespace.Utils })
  public apiError!: string

  @State('myTenant', { namespace: Namespace.App })
  public myTenant!: Tenant

  @Mutation('updateMyTenant', { namespace: Namespace.App })
  public updateMyTenantState: any

  @Action('updateMyTenant', { namespace: Namespace.App })
  public updateMyTenant: any

  @State('tenantContextDocuments', { namespace: Namespace.TenantContext })
  public tenantContextDocuments!: TenantContextDocument[]

  @Getter('tenantContextDocumentCount', { namespace: Namespace.TenantContext })
  public tenantContextDocumentCount!: number

  @Getter('tenantContextPagination', { namespace: Namespace.TenantContext })
  public tenantContextPagination!: TenantContextDocumentsPagination

  @Action('loadTenantContextDocuments', { namespace: Namespace.TenantContext })
  public loadTenantContextDocuments: any
  @Action('presignTenantContextFile', { namespace: Namespace.TenantContext })
  public presignTenantContextFile: any
  @Action('uploadTenantContextFile', { namespace: Namespace.TenantContext })
  public uploadTenantContextFile: any
  @Action('completeTenantContextFileUpload', { namespace: Namespace.TenantContext })
  public completeTenantContextFileUpload: any
  @Action('deleteTenantContextDocument', { namespace: Namespace.TenantContext })
  public deleteTenantContextDocument: any

  public $refs!: Vue['$refs'] & {
    deleteDocumentPromptYesNo: PromptYesNo
    uploadDocumentsDialog: UploadDocumentsDialog
  }

  private readonly contextSaveChangesSuccessMsg = 'Context changes saved'
  // UI toggle semantics are "context enabled", but persisted subscription setting
  // is GlobalAIOptOut where true means "disable context/AI features".
  private readonly globalAIOptOutFeatureKey: string = 'GlobalAIOptOut'

  private contextEnabled: boolean = true
  private pendingDeleteAssetGuid: string = ''
  // File preview is rendered as an in-page component (not a nested route iframe)
  // so it shares the same app/auth context as Team settings.
  private previewDialogVisible: boolean = false
  private previewFilename: string = 'Document preview'
  private previewReferenceId: number = 0
  private previewContentType: string = ''
  private previewTenantId: number = TenantHelpers.InvalidTenantID
  private previewOpeningDocuments: Record<string, boolean> = {}
  private tenantContextCurrentPage: number = 1
  private tenantContextItemsPerPage: number = 5
  private tenantContextItemsPerPageOptions: number[] = [5, 10, 25, 50]
  private isTenantContextDocumentsLoading: boolean = false
  private activeUploadSessionId: number = 0
  private isBeingDestroyed: boolean = false
  private contextWebsiteUrlInput: string = ''
  private contextInstructionsInput: string = ''
  private initialContextEnabled: boolean = true
  private initialContextWebsiteUrl: string = ''
  private initialContextInstructions: string = ''
  private isContextSaving: boolean = false

  @Watch('editedTenant', { immediate: true })
  private onEditedTenantChanged() {
    this.syncContextFormFromTenant()
  }

  private beforeDestroy() {
    this.isBeingDestroyed = true
    this.activeUploadSessionId++
  }

  private readonly maxTenantContextDocuments: number = TeamLibraryConst.LibraryEntryMaxReferencePDFs

  // Context toggle can only be changed by TeamAdmin or JigStaff super user
  private get contextToggleDisabled(): boolean {
    return this.lacksTenantManagePermission
  }

  private get hasReachedMaxDocuments(): boolean {
    return this.tenantContextDocumentCount >= this.maxTenantContextDocuments
  }

  private get hasContextChanges(): boolean {
    return (
      this.contextEnabled !== this.initialContextEnabled ||
      this.contextWebsiteUrlInput.trim() !== this.initialContextWebsiteUrl.trim() ||
      this.contextInstructionsInput !== this.initialContextInstructions
    )
  }

  private get canSaveContextChanges(): boolean {
    if (this.contextToggleDisabled || this.isContextSaving) {
      return false
    }
    return this.hasContextChanges
  }

  private get tenantContextTotalPages(): number {
    if (!this.tenantContextPagination || !this.tenantContextPagination.totalPages) {
      return 1
    }

    return Math.max(1, this.tenantContextPagination.totalPages)
  }

  private get tenantContextCanGoPrev(): boolean {
    return this.tenantContextCurrentPage > 1
  }

  private get tenantContextCanGoNext(): boolean {
    return this.tenantContextCurrentPage < this.tenantContextTotalPages
  }

  private get tenantContextPaginationSummary(): string {
    const totalRows = this.tenantContextDocumentCount

    if (totalRows <= 0) {
      return '0 - 0 / 0'
    }

    const rangeStart = (this.tenantContextCurrentPage - 1) * this.tenantContextItemsPerPage + 1
    const rangeEnd = Math.min(this.tenantContextCurrentPage * this.tenantContextItemsPerPage, totalRows)

    return `${rangeStart} - ${rangeEnd} / ${totalRows}`
  }

  public syncContextFormFromTenant() {
    const context = this.editedTenant.Context
    const contextWebsiteUrls = (context && (context.WebsiteUrls || context.websiteUrls)) || []
    const contextInstructions = context && (context.Instructions || context.instructions)
    const firstWebsiteUrl = contextWebsiteUrls.length > 0 ? contextWebsiteUrls[0] : ''
    const globalAIOptOut = this.getGlobalAIOptOutValue(this.editedTenant)

    const websiteUrl = firstWebsiteUrl && firstWebsiteUrl.Url ? firstWebsiteUrl.Url : ''
    const instructions = contextInstructions || ''

    // Invert persisted opt-out value into UI-friendly "enabled" toggle.
    this.contextEnabled = !globalAIOptOut
    this.contextWebsiteUrlInput = websiteUrl
    this.contextInstructionsInput = instructions
    this.initialContextEnabled = this.contextEnabled
    this.initialContextWebsiteUrl = websiteUrl
    this.initialContextInstructions = instructions
  }

  private getGlobalAIOptOutValue(tenant: Tenant): boolean {
    const subscription = tenant && tenant.Subscription ? tenant.Subscription : null
    const subscriptionFeatures = subscription && subscription.SubscriptionFeatures ? subscription.SubscriptionFeatures : null
    const customFeatures = subscriptionFeatures && subscriptionFeatures.CustomFeatures ? subscriptionFeatures.CustomFeatures : {}
    const customFeatureValue = customFeatures[this.globalAIOptOutFeatureKey]
    if (typeof customFeatureValue === 'boolean') {
      return customFeatureValue
    }

    const availableFeature =
      subscriptionFeatures && subscriptionFeatures.Available ? subscriptionFeatures.Available[this.globalAIOptOutFeatureKey] : null
    if (availableFeature && typeof availableFeature.Default === 'boolean') {
      return availableFeature.Default
    }

    return false
  }

  private async saveContextChanges() {
    if (!this.canSaveContextChanges) {
      return
    }

    this.isContextSaving = true

    try {
      const tenantCopy: Tenant = AppHelper.deepCopyNestedJsonSafeObject(this.editedTenant)

      if (tenantCopy.Context == null) {
        tenantCopy.Context = {}
      }

      tenantCopy.Context.Instructions = this.contextInstructionsInput

      const websiteUrl = this.contextWebsiteUrlInput.trim()
      tenantCopy.Context.WebsiteUrls = websiteUrl ? [{ Url: websiteUrl }] : []

      if (tenantCopy.Subscription == null) {
        tenantCopy.Subscription = AppHelper.deepCopyNestedJsonSafeObject(SubscriptionConst.defaultSubscription)
      }

      if (tenantCopy.Subscription.SubscriptionFeatures == null) {
        tenantCopy.Subscription.SubscriptionFeatures = AppHelper.deepCopyNestedJsonSafeObject(
          SubscriptionConst.defaultSubscription.SubscriptionFeatures
        )
      }

      // Context toggle copy:
      // UI toggle ON means "allow context use", while backend field
      // `GlobalAIOptOut=true` means the opposite (disable AI/context use).
      // Persist the inverse to keep compatibility with existing subscription feature shape.
      if (tenantCopy.Subscription.SubscriptionFeatures.CustomFeatures == null) {
        tenantCopy.Subscription.SubscriptionFeatures.CustomFeatures = {}
      }
      tenantCopy.Subscription.SubscriptionFeatures.CustomFeatures[this.globalAIOptOutFeatureKey] = !this.contextEnabled

      await this.updateMyTenant(tenantCopy)

      if (this.apiError === undefined || this.apiError.length === 0) {
        this.$emit('snackbar', this.contextSaveChangesSuccessMsg)
        this.$emit('context-saved')
      }
    } finally {
      this.isContextSaving = false
    }
  }

  private openUploadDocumentsDialog() {
    const remainingSlots = this.maxTenantContextDocuments - this.tenantContextDocumentCount
    this.$refs.uploadDocumentsDialog.show({ maxFiles: remainingSlots })
  }

  private canContinueUploadSession(uploadSessionId: number): boolean {
    return !this.isBeingDestroyed && this.activeUploadSessionId === uploadSessionId
  }

  private async onDocumentsImport(files: File[]) {
    if (!files || files.length === 0) {
      return
    }

    const remainingSlots = this.maxTenantContextDocuments - this.tenantContextDocumentCount
    if (remainingSlots <= 0) {
      return
    }
    const filesToUpload = files.slice(0, remainingSlots)

    const tenantId = this.editedTenant.ID
    const dialog = this.$refs.uploadDocumentsDialog
    const uploadSessionId = ++this.activeUploadSessionId

    try {
      // Upload files sequentially to avoid bursts of API calls.
      for (let index = 0; index < filesToUpload.length; index++) {
        if (!this.canContinueUploadSession(uploadSessionId)) {
          break
        }

        const file = filesToUpload[index]
        await this.uploadDocument(file, index, tenantId, dialog, uploadSessionId)
      }
    } catch (error) {
      if (this.canContinueUploadSession(uploadSessionId)) {
        dialog.markPendingAsError('Unexpected upload failure')
      }
    } finally {
      if (this.canContinueUploadSession(uploadSessionId)) {
        dialog.setUploading(false)
      }
    }
  }

  private async uploadDocument(
    file: File,
    index: number,
    tenantId: number,
    dialog: UploadDocumentsDialog,
    uploadSessionId: number
  ): Promise<boolean> {
    if (!this.canContinueUploadSession(uploadSessionId)) {
      return false
    }

    dialog.updateFileState(index, DocumentUploadState.UPLOADING)

    try {
      const presignedResult = await this.presignTenantContextFile({
        tenantId,
        filename: file.name,
        mime: file.type,
        size: file.size,
      })

      if (!presignedResult) {
        if (this.canContinueUploadSession(uploadSessionId)) {
          dialog.updateFileState(index, DocumentUploadState.ERROR, 'Failed to prepare upload')
        }
        return false
      }

      const uploadResult = await this.uploadTenantContextFile({
        ...presignedResult,
        file,
      })

      if (!uploadResult || !uploadResult.data) {
        if (this.canContinueUploadSession(uploadSessionId)) {
          dialog.updateFileState(index, DocumentUploadState.ERROR, 'Failed to upload file')
        }
        return false
      }

      const completeResult = await this.completeTenantContextFileUpload({
        ...uploadResult.data,
        tenantId,
      })

      if (!completeResult) {
        if (this.canContinueUploadSession(uploadSessionId)) {
          dialog.updateFileState(index, DocumentUploadState.ERROR, 'Failed to finalize upload')
        }
        return false
      }

      if (this.canContinueUploadSession(uploadSessionId)) {
        dialog.updateFileState(index, DocumentUploadState.DONE)
      }
      return true
    } catch (error) {
      if (this.canContinueUploadSession(uploadSessionId)) {
        dialog.updateFileState(index, DocumentUploadState.ERROR, 'Unexpected upload failure')
      }
      return false
    }
  }

  private async onDocumentsUploadComplete() {
    this.tenantContextCurrentPage = 1
    await this.loadTenantContextDocumentsPage(1)
  }

  private onDeleteDocument(assetGuid: string) {
    this.pendingDeleteAssetGuid = assetGuid
    this.$refs.deleteDocumentPromptYesNo.ShowPrompt()
  }

  private previewDocumentKey(doc: TenantContextDocument): string {
    return doc.assetGuid || `${doc.referenceId}`
  }

  private isPreviewOpening(doc: TenantContextDocument): boolean {
    return !!this.previewOpeningDocuments[this.previewDocumentKey(doc)]
  }

  private async onPreviewDocument(doc: TenantContextDocument) {
    if (!doc.referenceId) {
      return
    }

    const documentKey = this.previewDocumentKey(doc)
    if (this.previewOpeningDocuments[documentKey]) {
      return
    }
    this.$set(this.previewOpeningDocuments, documentKey, true)

    try {
      const tenantId = this.editedTenant && this.editedTenant.ID ? this.editedTenant.ID : this.myTenant.ID
      this.previewReferenceId = doc.referenceId
      this.previewFilename = doc.originalFilename || 'Document preview'
      this.previewContentType = doc.contentType || ''
      this.previewTenantId = tenantId > 0 ? tenantId : TenantHelpers.InvalidTenantID
      this.previewDialogVisible = true
    } finally {
      this.$delete(this.previewOpeningDocuments, documentKey)
    }
  }

  private onPreviewClose() {
    this.previewDialogVisible = false
  }

  @Watch('previewDialogVisible')
  private onPreviewDialogVisibleChanged(value: boolean) {
    if (!value) {
      this.previewReferenceId = 0
      this.previewFilename = 'Document preview'
      this.previewContentType = ''
      this.previewTenantId = TenantHelpers.InvalidTenantID
    }
  }

  private async confirmDeleteDocument() {
    if (!this.pendingDeleteAssetGuid) {
      return
    }

    // Cover the table immediately so users don't see stale rows between
    // delete confirmation and the subsequent list refresh.
    this.isTenantContextDocumentsLoading = true

    try {
      const result = await this.deleteTenantContextDocument(this.pendingDeleteAssetGuid)
      if (result) {
        await this.loadTenantContextDocumentsPage(this.tenantContextCurrentPage)
      }
    } finally {
      this.isTenantContextDocumentsLoading = false
      this.pendingDeleteAssetGuid = ''
    }
  }

  public async loadTenantContextDocumentsPage(page: number, canRetryOutOfRange: boolean = true) {
    const tenantId = this.editedTenant.ID
    if (tenantId === TenantHelpers.InvalidTenantID) {
      return
    }

    const targetPage = Math.max(1, page)
    this.isTenantContextDocumentsLoading = true

    try {
      const isLoaded = await this.loadTenantContextDocuments({
        tenantId,
        page: targetPage,
        limit: this.tenantContextItemsPerPage,
      })

      // Do not advance pagination UI on failed requests; keep showing the
      // previous page state with the last successfully loaded data.
      if (!isLoaded) {
        return
      }

      if (this.tenantContextPagination && this.tenantContextPagination.limit > 0) {
        this.tenantContextItemsPerPage = this.tenantContextPagination.limit
      }

      const totalPages = this.tenantContextTotalPages
      if (canRetryOutOfRange && targetPage > totalPages) {
        await this.loadTenantContextDocumentsPage(totalPages, false)
        return
      }

      this.tenantContextCurrentPage = Math.min(targetPage, totalPages)
    } finally {
      this.isTenantContextDocumentsLoading = false
    }
  }

  private async onContextDocumentsItemsPerPageChange() {
    if (this.isTenantContextDocumentsLoading) {
      return
    }

    this.tenantContextCurrentPage = 1
    await this.loadTenantContextDocumentsPage(1)
  }

  private async onContextDocumentsPrevPage() {
    if (this.isTenantContextDocumentsLoading || !this.tenantContextCanGoPrev) {
      return
    }

    await this.loadTenantContextDocumentsPage(this.tenantContextCurrentPage - 1)
  }

  private async onContextDocumentsNextPage() {
    if (this.isTenantContextDocumentsLoading || !this.tenantContextCanGoNext) {
      return
    }

    await this.loadTenantContextDocumentsPage(this.tenantContextCurrentPage + 1)
  }
}
